import { useCallback, useMemo, useState } from "react";
import { Button, Col, Container, Dropdown, Row } from "react-bootstrap";
import { CommonSelect, CstmPagination } from "../../../../components/Common/Common";
import { PRIVATE_CLIENT_DETAIL } from "config/paths";
import LoadingIndicator from "components/shared/LoadingIndicator";
import moment from "moment";
import LoadingOverlay from "components/shared/LoadingOverlay";
import ErrorComponent from "components/shared/ErrorComponent";
import { dateFormat } from "helpers/common";
import ClientsReportMenu from "components/shared/ReportComponents/ClientsReportMenu";
import { useGetFundingSourceListQuery } from "store/services/utility";
import { CLIENT_STATUS_OPTION, GRADE_OPTIONS } from "config/constants";
import { useDispatch, useSelector } from "react-redux";
import { initialFilterState, privateClientsFilter } from "store/slices/filter";
import { usePersistentState } from "hooks/usePersistentState";
import TableLayoutV2 from "components/TableLayoutV2";
import { Star } from "react-feather";
import { useFavouritePrivateClientMutation, useGetPrivateClientListQuery } from "store/services/clients/private";
import DebouncedSearch from "components/shared/DebouncedSearch";

const ClientPrivate = () => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = usePersistentState("private:page", 1);
  const [pageSize, setPageSize] = useState(10);
  const filter = useSelector(state => state?.filter?.privateClientsFilter);

  const { data, isLoading, isError, isFetching, refetch } = useGetPrivateClientListQuery({ page: currentPage, limit: pageSize, ...filter });
  const totalRecords = data?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const [favouriteClient, { isLoading: favouriteClientLoading }] = useFavouritePrivateClientMutation();

  // Funding source filter list
  const { data: fundingSourceList, isLoading: fundingSourceListLoading } = useGetFundingSourceListQuery();

  const fundingSourceOptions = useMemo(() => {
    if (!fundingSourceList?.length) return [];

    return fundingSourceList.map(item => ({ value: item.id, label: item?.name }))
  }, [fundingSourceList]);

  const handleFavouriteClient = async (id, favorite) => {
    const data = {
      favorite
    }
    try {
      await favouriteClient({ data, id }).unwrap()
      refetch();
    } catch (error) {
      console.log('error making the client favourite: ', error)
    }
  }


  const column = [
    {
      head: <div onClick={() => dispatch(privateClientsFilter({ ...filter, sort: filter?.sort?.column == "is_favorite" ? { column: "lastname", direction: "ASC" } : { column: "is_favorite", direction: "desc" } }))}>{filter?.sort?.column == "is_favorite" ? <Star fill="#93278F" stroke="#93278F" width={18} /> : <Star width={18} stroke="#8F8F8F" />}</div>,
      accessor: "is_favorite",
      isComponent: true,
      component: (item, ind) => (
        <h6 className="m-0 text-capitalize d-flex align-items-center gap-2"><div className="cursor-pointer" onClick={(e) => { e.stopPropagation(); handleFavouriteClient(item?.client_detail?.id, item?.client_detail?.is_favorite ? 0 : 1) }}>{item?.client_detail?.is_favorite ? <Star fill="#93278F" stroke="#93278F" width={18} /> : <Star width={18} stroke="#8F8F8F" />}</div></h6>
      ),
    },
    {
      head: "Last Name",
      accessor: "lastname",
      isComponent: true,
      sorter: true,
      component: (item, ind) => (
        <h6 className="m-0 fw-sbold text-capitalize">{item.lastname}</h6>
      ),
    },
    {
      head: "First Name",
      accessor: "firstname",
      sorter: true,
      isComponent: true,
      component: (item, ind) => (
        <h6 className="m-0 fw-sbold text-capitalize">{item.firstname}</h6>
      ),
    },
    {
      head: "DOB",
      accessor: "dob",
      sorter: true,
      isComponent: true,
      component: (item, ind) => (
        <>{item?.client_detail?.dob ? moment(item?.client_detail?.dob).format("DD/MM/YYYY") : "--"}</>
      ),
    },
    {
      head: <div className="w-100 text-center">Funding Source</div>,
      accessor: "FundingSource",
      isComponent: true,
      component: (item, ind) => (
        <Dropdown >
          <Dropdown.Toggle
            disabled
            className="border-light text-dark w-100 p-0"
            style={{ background: "#F3F3F3" }}
            id="dropdown-basic"
          >
            {item?.client_detail?.client_private_detail?.private_funding_source?.name || "--"}
            <span className="icn ms-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="5"
                viewBox="0 0 10 5"
                fill="none"
              >
                <path
                  d="M4.99997 4.27048L0.816406 0.0869141H9.18353L4.99997 4.27048Z"
                  fill="#1C1B1F"
                />
              </svg>
            </span>
          </Dropdown.Toggle>

          <Dropdown.Menu
            className="w-100"
            style={{ right: 0, left: "unset", minWidth: "unset" }}
          >
            <ul className="list-unstyled ps-0 mb-0">
              <li className="border-bottom">
                <Button className="border-0 py-1 px-2 " variant="transparent">
                  Workshop
                </Button>
              </li>
              <li className="border-bottom">
                <Button className="border-0 py-1 px-2 " variant="transparent">
                  Consultation
                </Button>
              </li>
              <li className="border-bottom">
                <Button className="border-0 py-1 px-2 " variant="transparent">
                  Assessment
                </Button>
              </li>
              <li className="border-bottom">
                <Button className="border-0 py-1 px-2 " variant="transparent">
                  Treatment
                </Button>
              </li>
              <li className="border-bottom">
                <Button className="border-0 py-1 px-2 " variant="transparent">
                  Discharge
                </Button>
              </li>
            </ul>
          </Dropdown.Menu>
        </Dropdown>
      ),
    },
    {
      head: "Start Date",
      accessor: "StartDate",
      isComponent: true,
      component: (item, ind) => (
        <>{dateFormat(item?.client_detail?.client_private_detail?.start_date)}</>
      ),
    },
    {
      head: "End Date",
      accessor: "EndDate",
      isComponent: true,
      component: (item, ind) => (
        <>{dateFormat(item?.client_detail?.client_private_detail?.end_date)}</>
      ),
    },
    {
      head: "Action",
      accessor: "",
      isComponent: true,
      component: (item, index) => (
        <ClientsReportMenu
          index={index}
          v2={true}
          client={item}
        />
      ),
    },
  ];

  const resetFilter = () => {
    dispatch(privateClientsFilter(initialFilterState.privateClientsFilter));
    setCurrentPage(1)
  };

  const handlePageChange = (page) => {
    window.scrollTo(0, 0);
    setCurrentPage(page);
  };

  const handleFilterChange = (key) => (value) => {
    dispatch(privateClientsFilter({ ...filter, [key]: value }))
    setCurrentPage(1); // Reset to first page when filter changes
  };

  return (
    <>
      {" "}
      <section className="client position-relative py-4">
        <Container fluid>
          {
            isLoading ? <LoadingIndicator /> : <Row>
              <Col lg="12" className="my-2">
                <Col lg="12">
                  <div className="left classroom-conslutation">
                    <h6 className="m-0 fw-bold main-head themeBlue">Private Clients</h6>
                  </div>
                </Col>
                {/* <div className="TableFilter d-flex align-items-center justify-content-between gap-10 flex-wrap">
                  <div className="left"> */}
                {/* <TableHeader
                      heading={"Home Education Caseload"}
                      handleFilterChange={handleFilterChange}
                      filter={filter}
                      resetFilter={resetFilter}
                      searchPlaceholder="Client First, Last Name"
                    /> */}
                <Row className="reports-v2 my-3 d-flex justify-content-end classroom-conslutation">
                  <Col md='3'>
                  </Col>
                  <Col md='4'>
                    <div className="d-flex w-100 align-items-center gap-3">
                      <label className="discharge-screen-label w-80 mb-0">Funding Source</label>
                      <CommonSelect
                        placeholder={"Select"}
                        option={fundingSourceOptions}
                        onChange={(e) => handleFilterChange("funding_source")(e)}
                        value={filter?.funding_source}
                        isFilterV2
                        isClearable
                        styles={{
                          container: (provided) => ({
                            ...provided,
                            width: "100%",
                          }),
                        }}
                        isLoading={fundingSourceListLoading}
                      />
                    </div>
                  </Col>
                  <Col md='2'>
                    <div className="d-flex w-100 align-items-center gap-3">
                      <label className="discharge-screen-label mb-0">Status</label>
                      <CommonSelect
                        placeholder={"Select"}
                        option={CLIENT_STATUS_OPTION}
                        onChange={(e) => handleFilterChange("status")(e)}
                        value={filter?.status}
                        isFilterV2
                        // isClearable
                        // isLoading={schoolListLoading || schoolListFetching}
                        styles={{
                          container: (provided) => ({
                            ...provided,
                            width: "100%", // ✅ Ensures the whole Select container is full width
                          }),
                        }}
                      />
                    </div>
                  </Col>
                  <Col lg="3">
                    <div className="d-flex w-100 align-items-center gap-3">
                      <label className="discharge-screen-label mb-0">Search</label>
                      {/* <input
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className={` readonly-box h-auto search-input-filter-v2`}
                        placeholder="Type..."
                      /> */}
                      <DebouncedSearch
                        defaultValue={filter?.search}
                        onSearchChange={handleFilterChange("search")}
                        className={` readonly-box h-auto search-input-filter-v2`}
                        showSearchIcon={false}
                      />
                    </div>
                  </Col>
                </Row>
                {/* </div> */}

                {/* </div> */}

                <div className="table-outer">
                  {isLoading ? (
                    <div style={{ height: "200px" }}>
                      <LoadingIndicator />
                    </div>
                  ) : isError ? (
                    <ErrorComponent />
                  ) : (
                    <>
                      {(isFetching || favouriteClientLoading) && <LoadingOverlay />}
                      <TableLayoutV2
                        data={data?.records || []}
                        column={column}
                        url={PRIVATE_CLIENT_DETAIL.replace("/:id", "")}
                        handleSort={handleFilterChange("sort")}
                        filter={filter}
                      />
                    </>
                  )}
                  <CstmPagination
                    totalPages={totalPages}
                    activePage={currentPage}
                    className={'paginationV2'}
                    handlePageChange={handlePageChange}
                  />
                </div>
              </Col>
            </Row>
          }
        </Container>
      </section>
    </>
  );
};

export default ClientPrivate;
